import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Loader2, Download, RefreshCw, Calendar, DollarSign, TrendingUp, TrendingDown, Users, Sparkles,
  FileSpreadsheet, Minus, ShieldAlert, Clock, Crown, Gift, Receipt, UsersRound, Bot, Trophy,
  MessageSquare, Percent, Wallet, PieChart, Star, Megaphone, Moon, ClipboardEdit, MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHEET_OPTIONS = [
  { id: "users", label: "Usuarios", icon: "👥" },
  { id: "plans", label: "Pagos / Trabajos", icon: "💰" },
  { id: "appointments", label: "Citas", icon: "📅" },
  { id: "readings", label: "Lecturas", icon: "🔮" },
  { id: "broadcast_msgs", label: "Mensajes Masivos", icon: "📣" },
];
const SECTIONS = [
  { id: "finanzas", label: "Finanzas", icon: DollarSign },
  { id: "cobranza", label: "Cobranza", icon: ShieldAlert },
  { id: "clientes", label: "Clientes", icon: Crown },
  { id: "promos", label: "Promociones", icon: Gift },
  { id: "equipo", label: "Equipo & Sistema", icon: UsersRound },
  { id: "export", label: "Exportar", icon: FileSpreadsheet },
];

const money = (n, cur = "") => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}${cur ? ` ${cur}` : ""}`;
const fmtDT = (iso) => { try { return new Date(iso).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }); } catch { return iso || "—"; } };
const openChat = (user_id, user_name) => window.dispatchEvent(new CustomEvent("admin:openChat", { detail: { user_id, user_name } }));
const openDossier = (user_id) => window.dispatchEvent(new CustomEvent("admin:openDossier", { detail: { user_id } }));

/* ---------- building blocks ---------- */
const Growth = ({ value, invert = false }) => {
  if (value === null || value === undefined) return <span className="text-[10px] text-neutral-600">sin dato previo</span>;
  const up = value > 0, flat = value === 0;
  const good = invert ? !up : up;
  const cls = flat ? "text-neutral-400" : good ? "text-emerald-300" : "text-rose-300";
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  return <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${cls}`}><Icon className="w-3 h-3" />{value > 0 ? "+" : ""}{value}%</span>;
};

const StatCard = ({ icon: Icon, color = "gold", label, value, sub, growth, testid }) => {
  const c = {
    gold: "text-[#d4af37] from-[#d4af37]/15 to-transparent border-[#d4af37]/25",
    emerald: "text-emerald-300 from-emerald-500/15 to-transparent border-emerald-500/25",
    amber: "text-amber-300 from-amber-500/15 to-transparent border-amber-500/25",
    sky: "text-sky-300 from-sky-500/15 to-transparent border-sky-500/25",
    purple: "text-purple-300 from-purple-500/15 to-transparent border-purple-500/25",
    rose: "text-rose-300 from-rose-500/15 to-transparent border-rose-500/25",
  }[color];
  return (
    <div data-testid={testid} className={`bg-gradient-to-br ${c} border rounded-xl p-3`}>
      <div className="flex items-center justify-between gap-2 text-[10px] uppercase tracking-widest text-neutral-400"><span className="flex items-center gap-1.5"><Icon className="w-3 h-3" /> {label}</span>{growth !== undefined && <Growth value={growth} />}</div>
      <div className={`text-xl font-bold mt-1 ${c.split(" ")[0]}`}>{value}</div>
      {sub && <div className="text-[10px] text-neutral-500 mt-0.5">{sub}</div>}
    </div>
  );
};

const Card = ({ title, icon: Icon, children, testid, right, className = "" }) => (
  <div className={`glass-dark rounded-2xl p-4 ${className}`} data-testid={testid}>
    <div className="flex items-center justify-between gap-2 mb-3">
      <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Icon className="w-4 h-4 text-[#d4af37]" /> {title}</h3>
      {right}
    </div>
    {children}
  </div>
);

const Bar = ({ pct, tone = "gold" }) => (
  <div className="w-full bg-black/40 rounded-full h-2 overflow-hidden">
    <div className={`h-full rounded-full ${tone === "emerald" ? "bg-gradient-to-r from-emerald-500 to-emerald-300" : tone === "rose" ? "bg-gradient-to-r from-rose-500 to-rose-300" : "bg-gradient-to-r from-[#d4af37] to-amber-300"}`} style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
  </div>
);

const Empty = ({ children }) => <p className="text-neutral-500 italic text-xs">{children}</p>;

/* ---------- sections ---------- */
const FinanzasSection = ({ data }) => {
  const g = data.comparison?.growth;
  const cur = data.comparison?.current;
  const prev = data.comparison?.previous;
  return (
    <div className="space-y-4">
      <Card title={`Comparativa vs. periodo anterior${data.comparison?.previous_period ? ` (${data.comparison.previous_period.from} → ${data.comparison.previous_period.to})` : ""}`} icon={TrendingUp} testid="reports-comparison">
        {!prev ? <Empty>La comparativa no aplica cuando el periodo es &quot;Todo&quot;.</Empty> : (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <StatCard icon={DollarSign} color="emerald" label="Cobrado" value={money(cur.collected_total)} sub={`antes ${money(prev.collected_total)}`} growth={g.collected} testid="cmp-collected" />
            <StatCard icon={Wallet} color="gold" label="Planes creados" value={cur.plans_created} sub={`antes ${prev.plans_created}`} growth={g.plans_created} testid="cmp-plans" />
            <StatCard icon={Receipt} color="sky" label="Liquidados" value={cur.plans_liquidated} sub={`antes ${prev.plans_liquidated}`} growth={g.plans_liquidated} testid="cmp-liquidated" />
            <StatCard icon={Users} color="purple" label="Usuarios nuevos" value={cur.new_users} sub={`antes ${prev.new_users}`} growth={g.new_users} testid="cmp-users" />
            <StatCard icon={Calendar} color="amber" label="Citas" value={cur.appointments} sub={`antes ${prev.appointments}`} growth={g.appointments} testid="cmp-appts" />
            <StatCard icon={Sparkles} color="rose" label="Lecturas" value={cur.readings} sub={`antes ${prev.readings}`} growth={g.readings} testid="cmp-readings" />
          </div>
        )}
      </Card>

      {Object.entries(data.revenue_by_currency || {}).map(([c, v]) => {
        if (!v.plans_count && !v.collected_period && !v.pending_portfolio) return null;
        const flag = c === "USD" ? "🇺🇸" : c === "MXN" ? "🇲🇽" : "💱";
        return (
          <Card key={c} title={`Finanzas en ${c === "USD" ? "Dólares (USD)" : c === "MXN" ? "Pesos Mexicanos (MXN)" : c}`} icon={DollarSign} testid={`currency-block-${c}`}
            right={<span className="text-xl">{flag}</span>}>
            <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3 mb-3">
              <StatCard icon={DollarSign} color="emerald" label="Cobrado en periodo" value={money(v.collected_period, c)} sub={`${v.installments_period} abonos`} growth={g?.by_currency?.[c]} testid={`kpi-collected-${c}`} />
              <StatCard icon={Wallet} color="sky" label="Acordado (planes nuevos)" value={money(v.agreed, c)} sub={`${v.plans_count} planes · ${v.paid_count} pagados`} testid={`kpi-agreed-${c}`} />
              <StatCard icon={ShieldAlert} color="amber" label="Cartera activa pendiente" value={money(v.pending_portfolio, c)} sub={`${v.active_plans} planes con saldo`} testid={`kpi-pending-${c}`} />
              <StatCard icon={Receipt} color="gold" label="Ticket promedio" value={money(v.avg_ticket, c)} sub="por plan liquidado" testid={`kpi-avg-${c}`} />
              <StatCard icon={Percent} color="rose" label="Descuentos otorgados" value={money(v.discounts_total, c)} sub="ofertas + promos" testid={`kpi-discounts-${c}`} />
              <div data-testid={`kpi-rate-${c}`} className="bg-gradient-to-br from-purple-500/15 to-transparent border border-purple-500/25 rounded-xl p-3">
                <div className="text-[10px] uppercase tracking-widest text-neutral-400 flex items-center gap-1.5"><PieChart className="w-3 h-3" /> Tasa de cobranza</div>
                <div className="text-xl font-bold mt-1 text-purple-300">{v.collection_rate}%</div>
                <div className="mt-1.5"><Bar pct={v.collection_rate} tone={v.collection_rate >= 70 ? "emerald" : v.collection_rate >= 40 ? "gold" : "rose"} /></div>
              </div>
            </div>
          </Card>
        );
      })}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {Object.entries(data.by_service_by_currency || {}).map(([c, services]) => services.length === 0 ? null : (
          <Card key={c} title={`Ingresos por servicio · ${c}`} icon={DollarSign} testid={`by-service-${c}`}>
            <div className="space-y-2">
              {services.map((s) => {
                const max = Math.max(...services.map((x) => x.amount), 1);
                return (
                  <div key={s.service} className="space-y-0.5">
                    <div className="flex justify-between text-xs"><span className="text-neutral-200 truncate">{s.service}</span><span className="text-emerald-300 font-mono whitespace-nowrap">{money(s.amount, c)}</span></div>
                    <Bar pct={(s.amount / max) * 100} tone={c === "USD" ? "emerald" : "gold"} />
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
        {Object.entries(data.daily_revenue_by_currency || {}).map(([c, series]) => series.length === 0 ? null : (
          <Card key={c} title={`Ingresos diarios · ${c}`} icon={TrendingUp} testid={`daily-revenue-${c}`}>
            <div className="flex items-end gap-1 h-32">
              {(() => { const max = Math.max(...series.map((x) => x.amount), 1); return series.map((d) => (
                <div key={d.date} title={`${d.date}: ${money(d.amount, c)}`} className={`flex-1 min-w-[6px] ${c === "USD" ? "bg-gradient-to-t from-emerald-500 to-emerald-300" : "bg-gradient-to-t from-[#d4af37] to-amber-300"} rounded-t`} style={{ height: `${Math.max(2, (d.amount / max) * 100)}%` }} />
              )); })()}
            </div>
            <div className="text-[10px] text-neutral-500 text-center mt-1">{series[0].date} → {series[series.length - 1].date}</div>
          </Card>
        ))}
        <Card title="Métodos de pago (periodo)" icon={Wallet} testid="payment-methods">
          {data.payment_methods.length === 0 ? <Empty>Sin abonos en el periodo.</Empty> : (
            <div className="space-y-2">
              {data.payment_methods.map((m) => {
                const max = Math.max(...data.payment_methods.map((x) => x.amount), 1);
                return (
                  <div key={m.method} className="space-y-0.5">
                    <div className="flex justify-between text-xs"><span className="text-neutral-200 capitalize">{m.method}</span><span className="text-neutral-400">{m.count} · <span className="text-emerald-300 font-mono">{money(m.amount)}</span></span></div>
                    <Bar pct={(m.amount / max) * 100} tone="emerald" />
                  </div>
                );
              })}
            </div>
          )}
        </Card>
        <Card title="Estado de planes" icon={Wallet} testid="plans-breakdown">
          <div className="space-y-1.5 text-xs">
            {[["Creados en periodo", data.plans.total, "text-white"], ["Liquidados", data.plans.paid, "text-emerald-300"], ["Activos", data.plans.active, "text-amber-300"], ["Cancelados", data.plans.cancelled, "text-rose-300"], ["Cartera activa total (todos)", data.plans.portfolio_active, "text-sky-300"]].map(([k, v, c]) => (
              <div key={k} className="flex justify-between border-b border-white/5 pb-1"><span className="text-neutral-300">{k}</span><span className={`font-mono ${c}`}>{v}</span></div>
            ))}
            <div className="pt-1 text-[10px] text-neutral-500">Citas: {Object.entries(data.appointments.by_status || {}).map(([k, v]) => `${k} ${v}`).join(" · ") || "—"}</div>
            <div className="text-[10px] text-neutral-500">Lecturas: {Object.entries(data.readings.by_status || {}).map(([k, v]) => `${k} ${v}`).join(" · ") || "—"}</div>
          </div>
        </Card>
      </div>
    </div>
  );
};

const CobranzaSection = ({ data }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      <StatCard icon={ShieldAlert} color="rose" label="Promesas vencidas" value={data.overdue.count} sub={Object.entries(data.overdue.total_by_currency).map(([c, v]) => money(v, c)).join(" · ") || "sin saldo vencido"} testid="kpi-overdue" />
      <StatCard icon={Clock} color="amber" label="Próximos cobros 7d" value={data.upcoming_7d.length} sub={money(data.upcoming_7d.reduce((s, u) => s + u.remaining, 0))} testid="kpi-upcoming" />
      <StatCard icon={Receipt} color="amber" label="Abonos sin reflejar" value={data.unreflected?.count || 0} sub={Object.entries(data.unreflected?.total_by_currency || {}).map(([c, v]) => money(v, c)).join(" · ") || "todo reflejado"} testid="kpi-unreflected" />
      <StatCard icon={Wallet} color="sky" label="Planes con saldo" value={data.plans.portfolio_active} testid="kpi-portfolio" />
      <StatCard icon={Receipt} color="emerald" label="Abonos en periodo" value={data.ledger.length >= 40 ? "40+" : data.ledger.length} testid="kpi-ledger-count" />
    </div>
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <Card title="Cartera vencida" icon={ShieldAlert} testid="overdue-table">
        {data.overdue.items.length === 0 ? <Empty>Sin promesas de pago vencidas. 🎉</Empty> : (
          <div className="overflow-x-auto"><table className="w-full text-xs">
            <thead><tr className="text-[10px] uppercase tracking-widest text-neutral-500 text-left"><th className="pb-2">Cliente</th><th className="pb-2">Trabajo</th><th className="pb-2 text-right">Debe</th><th className="pb-2 text-right">Días</th><th className="pb-2" /></tr></thead>
            <tbody>{data.overdue.items.map((o) => (
              <tr key={o.plan_id} className="border-t border-white/5" data-testid={`overdue-row-${o.plan_id}`}>
                <td className="py-1.5"><button type="button" onClick={() => openDossier(o.user_id)} className="text-white hover:text-[#d4af37] text-left">{o.user_name || o.user_visible_id}</button></td>
                <td className="py-1.5 text-neutral-400 truncate max-w-[160px]">{o.service_name}</td>
                <td className="py-1.5 text-right text-rose-300 font-mono whitespace-nowrap">{money(o.remaining, o.currency)}</td>
                <td className="py-1.5 text-right text-rose-200">{o.days_overdue}</td>
                <td className="py-1.5 text-right"><button type="button" onClick={() => openChat(o.user_id, o.user_name)} className="p-1 rounded-full bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25" title="Chat"><MessageCircle className="w-3.5 h-3.5" /></button></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </Card>
      <Card title="Próximos cobros (7 días)" icon={Clock} testid="upcoming-table">
        {data.upcoming_7d.length === 0 ? <Empty>Sin cobros programados en los próximos 7 días.</Empty> : (
          <div className="space-y-1.5">{data.upcoming_7d.map((u) => (
            <div key={u.plan_id} className="flex justify-between gap-2 text-xs bg-black/40 border border-white/5 rounded-lg px-2.5 py-1.5" data-testid={`upcoming-row-${u.plan_id}`}>
              <div className="min-w-0"><div className="text-white truncate">{u.user_name}</div><div className="text-[10px] text-neutral-500 truncate">{u.service_name}</div></div>
              <div className="text-right whitespace-nowrap"><div className="text-[#d4af37] font-mono">{money(u.remaining, u.currency)}</div><div className="text-[10px] text-neutral-500">{u.due_date}</div></div>
            </div>
          ))}</div>
        )}
      </Card>
    </div>
    <Card title="Ledger de abonos (periodo)" icon={Receipt} testid="ledger-table">
      {data.ledger.length === 0 ? <Empty>Sin abonos en el periodo.</Empty> : (
        <div className="overflow-x-auto max-h-80 overflow-y-auto"><table className="w-full text-xs">
          <thead className="sticky top-0 bg-[#0a0a0a]"><tr className="text-[10px] uppercase tracking-widest text-neutral-500 text-left"><th className="pb-2">Fecha</th><th className="pb-2">Cliente</th><th className="pb-2">Trabajo</th><th className="pb-2">Método</th><th className="pb-2">Estado</th><th className="pb-2">Registró</th><th className="pb-2 text-right">Monto</th></tr></thead>
          <tbody>{data.ledger.map((l, i) => (
            <tr key={`${l.plan_id}-${l.at}-${i}`} className="border-t border-white/5">
              <td className="py-1.5 text-neutral-400 whitespace-nowrap">{fmtDT(l.at)}</td>
              <td className="py-1.5 text-white">{l.user_name}</td>
              <td className="py-1.5 text-neutral-400 truncate max-w-[180px]">{l.service_name}</td>
              <td className="py-1.5 text-neutral-400">{l.method || "—"}</td>
              <td className="py-1.5">{l.reflected === false ? <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-200 border border-amber-400/40">Sin reflejar</span> : <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300">Reflejado</span>}</td>
              <td className="py-1.5 text-neutral-500">{l.by}</td>
              <td className="py-1.5 text-right text-emerald-300 font-mono whitespace-nowrap">{money(l.amount, l.currency)}</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
    </Card>
  </div>
);

const ClientesSection = ({ data }) => {
  const entries = Object.entries(data.top_clients || {});
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Users} color="purple" label="Usuarios nuevos" value={data.users.new} sub={`/ ${data.users.total} total`} growth={data.comparison?.growth?.new_users} testid="kpi-newusers" />
        <StatCard icon={Users} color="emerald" label="En línea ahora" value={data.users.online} testid="kpi-online" />
        <StatCard icon={ShieldAlert} color="rose" label="Bloqueados" value={data.users.blocked} testid="kpi-blocked" />
        <StatCard icon={Crown} color="gold" label="Clientes con pagos" value={entries.reduce((s, [, l]) => s + l.length, 0)} sub="top 10 por moneda" testid="kpi-clients" />
      </div>
      {entries.length === 0 ? <Card title="Ranking de clientes" icon={Crown}><Empty>Aún no hay clientes con pagos.</Empty></Card> : entries.map(([c, list]) => (
        <Card key={c} title={`Top clientes · ${c}`} icon={Crown} testid={`top-clients-${c}`}>
          <div className="space-y-1.5">{list.map((cl, i) => {
            const max = Math.max(...list.map((x) => Math.max(x.paid_period, x.paid_total)), 1);
            return (
              <div key={cl.user_id} className="flex items-center gap-3 text-xs" data-testid={`top-client-${cl.user_id}`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${i === 0 ? "bg-[#d4af37] text-black" : i === 1 ? "bg-neutral-300 text-black" : i === 2 ? "bg-amber-700 text-white" : "bg-white/10 text-neutral-300"}`}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-2"><button type="button" onClick={() => openDossier(cl.user_id)} className="text-white truncate hover:text-[#d4af37] text-left">{cl.user_name || cl.user_visible_id}</button><span className="text-emerald-300 font-mono whitespace-nowrap">{money(cl.paid_period, c)} <span className="text-neutral-600">/ {money(cl.paid_total, c)} hist.</span></span></div>
                  <Bar pct={(Math.max(cl.paid_period, cl.paid_total * 0.15) / max) * 100} tone="emerald" />
                  <div className="text-[10px] text-neutral-500 mt-0.5">{cl.plans} trabajo(s){cl.remaining > 0 ? ` · debe ${money(cl.remaining, c)}` : " · al corriente"}</div>
                </div>
                <button type="button" onClick={() => openChat(cl.user_id, cl.user_name)} className="p-1.5 rounded-full bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25" title="Chat"><MessageCircle className="w-3.5 h-3.5" /></button>
              </div>
            );
          })}</div>
        </Card>
      ))}
    </div>
  );
};

const PromosSection = ({ data }) => {
  const p = data.promotions;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        <StatCard icon={Clock} color="amber" label="Activas" value={p.active} sub={`${money(p.discount_active_total)} en juego`} testid="promo-active" />
        <StatCard icon={Gift} color="emerald" label="Liquidadas c/ promo" value={p.accepted} sub={`${money(p.discount_accepted_total)} descontado`} testid="promo-accepted" />
        <StatCard icon={TrendingDown} color="rose" label="Expiradas" value={p.expired} testid="promo-expired" />
        <StatCard icon={Minus} color="sky" label="Canceladas" value={p.cancelled} testid="promo-cancelled" />
        <StatCard icon={Calendar} color="purple" label="Prorrogadas" value={p.extended} testid="promo-extended" />
        <StatCard icon={PieChart} color="gold" label="Tasa de aceptación" value={`${p.acceptance_rate}%`} sub={`${p.created_period} creadas / ${p.accepted_period} liquidadas en periodo`} testid="promo-rate" />
        <StatCard icon={Percent} color="emerald" label="Ofertas (periodo)" value={p.offers_period_count} sub={`${money(p.offers_period_total)} descontado`} testid="offers-period" />
      </div>
      <Card title="Promociones activas ahora" icon={Gift} testid="promo-active-list">
        {p.active_list.length === 0 ? <Empty>No hay promociones activas.</Empty> : (
          <div className="overflow-x-auto"><table className="w-full text-xs">
            <thead><tr className="text-[10px] uppercase tracking-widest text-neutral-500 text-left"><th className="pb-2">Cliente</th><th className="pb-2">Trabajo</th><th className="pb-2 text-right">Descuento</th><th className="pb-2 text-right">Paga</th><th className="pb-2">Vence</th><th className="pb-2">Prórroga</th><th className="pb-2">Creó</th></tr></thead>
            <tbody>{p.active_list.map((a) => (
              <tr key={a.promo_id} className="border-t border-white/5" data-testid={`promo-active-row-${a.promo_id}`}>
                <td className="py-1.5"><button type="button" onClick={() => openDossier(a.user_id)} className="text-white hover:text-[#d4af37] text-left">{a.user_name}</button></td>
                <td className="py-1.5 text-neutral-400 truncate max-w-[160px]">{a.service_name}</td>
                <td className="py-1.5 text-right text-emerald-300 font-mono">−{money(a.discount_amount, a.currency)}</td>
                <td className="py-1.5 text-right text-[#d4af37] font-mono">{money(a.new_remaining, a.currency)}</td>
                <td className="py-1.5 text-amber-300 whitespace-nowrap">{fmtDT(a.valid_until)}</td>
                <td className="py-1.5 text-sky-300">{a.extended_days_total ? `+${a.extended_days_total}d` : "—"}</td>
                <td className="py-1.5 text-neutral-500">{a.created_by}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </Card>
    </div>
  );
};

const EquipoSection = ({ data }) => {
  const s = data.system;
  return (
    <div className="space-y-4">
      <Card title="Actividad del equipo admin (periodo)" icon={UsersRound} testid="team-activity">
        {data.team_activity.length === 0 ? <Empty>Sin actividad registrada en el periodo.</Empty> : (
          <div className="overflow-x-auto"><table className="w-full text-xs">
            <thead><tr className="text-[10px] uppercase tracking-widest text-neutral-500 text-left"><th className="pb-2">Admin</th><th className="pb-2 text-right">Abonos</th><th className="pb-2 text-right">Cobrado</th><th className="pb-2 text-right">Planes</th><th className="pb-2 text-right">Promos</th><th className="pb-2 text-right">Ofertas</th><th className="pb-2 text-right">Citas</th></tr></thead>
            <tbody>{data.team_activity.map((t) => (
              <tr key={t.admin} className="border-t border-white/5" data-testid={`team-row-${t.admin}`}>
                <td className="py-1.5 text-white font-semibold">{t.admin}</td>
                <td className="py-1.5 text-right text-neutral-300">{t.installments}</td>
                <td className="py-1.5 text-right text-emerald-300 font-mono whitespace-nowrap">{Object.entries(t.collected).map(([c, v]) => money(v, c)).join(" · ") || "—"}</td>
                <td className="py-1.5 text-right text-neutral-300">{t.plans_created}</td>
                <td className="py-1.5 text-right text-neutral-300">{t.promos_created}</td>
                <td className="py-1.5 text-right text-neutral-300">{t.offers_applied}</td>
                <td className="py-1.5 text-right text-neutral-300">{t.appointments_created}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </Card>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <StatCard icon={Bot} color="purple" label="Vidente · sesiones" value={s.vidente_sessions} sub={`${s.vidente_active_grants} accesos activos`} testid="sys-vidente" />
        <StatCard icon={Star} color="gold" label="Vidente · rating" value={s.vidente_rating_avg || "—"} sub={`${s.vidente_rating_count} reseñas`} testid="sys-rating" />
        <StatCard icon={Trophy} color="amber" label="Sorteos" value={s.sorteos_period} sub={`${s.sorteos_active} activos ahora`} testid="sys-sorteos" />
        <StatCard icon={MessageSquare} color="emerald" label="Mensajes" value={s.messages_period} sub={`${s.messages_from_users} de clientes`} testid="sys-messages" />
        <StatCard icon={Megaphone} color="sky" label="Broadcasts" value={s.broadcasts_period} testid="sys-broadcasts" />
        <StatCard icon={Moon} color="purple" label="Horóscopo activo" value={s.horoscope_active} testid="sys-horoscope" />
        <StatCard icon={Receipt} color="amber" label="Solicitudes pago" value={s.payment_requests_pending} sub="pendientes" testid="sys-payreq" />
        <StatCard icon={ClipboardEdit} color="rose" label="Cambios de perfil" value={s.profile_requests_pending} sub="pendientes" testid="sys-profile" />
        <StatCard icon={Star} color="emerald" label="Testimonios" value={s.testimonials_period} sub="en periodo" testid="sys-testimonials" />
      </div>
    </div>
  );
};

/* ---------- main ---------- */
export const AdminReportsTab = () => {
  const { authHeader } = useAuth();
  const [period, setPeriod] = useState("month");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [section, setSection] = useState("finanzas");
  const [sheets, setSheets] = useState(["users", "plans", "appointments", "readings"]);
  const [exporting, setExporting] = useState(false);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (dateFrom && dateTo) { p.set("date_from", dateFrom); p.set("date_to", dateTo); } else p.set("period", period);
    return p;
  }, [period, dateFrom, dateTo]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/reports/summary?${params}`, { headers: authHeader() });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando reporte");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  const exportExcel = async () => {
    if (sheets.length === 0) return toast.error("Selecciona al menos una hoja");
    setExporting(true);
    try {
      const p = new URLSearchParams(params); p.set("sheets", sheets.join(","));
      const r = await axios.get(`${API}/admin/reports/export?${p}`, { headers: authHeader(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `crisangel-reporte-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch { toast.error("Error exportando"); }
    finally { setExporting(false); }
  };
  const toggleSheet = (id) => setSheets((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  return (
    <div className="space-y-4" data-testid="admin-reports-tab">
      <div className="glass-dark rounded-2xl p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[160px]">
          <Label className="text-xs uppercase tracking-widest text-neutral-300">Periodo</Label>
          <Select value={period} onValueChange={(v) => { setPeriod(v); setDateFrom(""); setDateTo(""); }}>
            <SelectTrigger className="bg-black/40 border-white/10 text-white text-sm" data-testid="report-period"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0a0a0a] border-[#d4af37]/30 text-white">
              <SelectItem value="week">Última semana</SelectItem>
              <SelectItem value="month">Último mes</SelectItem>
              <SelectItem value="year">Último año</SelectItem>
              <SelectItem value="all">Todo</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs uppercase tracking-widest text-neutral-300">Desde</Label><Input data-testid="report-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-black/40 border-white/10 text-white text-sm" /></div>
        <div><Label className="text-xs uppercase tracking-widest text-neutral-300">Hasta</Label><Input data-testid="report-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-black/40 border-white/10 text-white text-sm" /></div>
        <button data-testid="report-reload" onClick={load} disabled={loading} className="btn-outline-gold px-3 py-2 rounded-full text-xs uppercase tracking-widest flex items-center gap-2"><RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Actualizar</button>
        {data && <span className="text-[10px] text-neutral-500 w-full sm:w-auto">Periodo analizado: {data.period.from} → {data.period.to}</span>}
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="report-sections">
        {SECTIONS.map((s) => (
          <button key={s.id} type="button" onClick={() => setSection(s.id)} data-testid={`report-section-${s.id}`}
            className={`flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full border transition ${section === s.id ? "bg-[#d4af37] text-black border-[#d4af37] font-bold" : "border-white/10 text-neutral-400 hover:text-white hover:border-white/25"}`}>
            <s.icon className="w-3.5 h-3.5" /> {s.label}
            {s.id === "cobranza" && data?.overdue?.count > 0 && <span className={`ml-0.5 rounded-full px-1.5 text-[9px] ${section === s.id ? "bg-black/30" : "bg-rose-500 text-white"}`}>{data.overdue.count}</span>}
          </button>
        ))}
      </div>

      {loading || !data ? (
        <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-[#d4af37] animate-spin" /></div>
      ) : (
        <>
          {section === "finanzas" && <FinanzasSection data={data} />}
          {section === "cobranza" && <CobranzaSection data={data} />}
          {section === "clientes" && <ClientesSection data={data} />}
          {section === "promos" && <PromosSection data={data} />}
          {section === "equipo" && <EquipoSection data={data} />}
          {section === "export" && (
            <Card title="Exportar a Excel" icon={FileSpreadsheet} testid="export-card">
              <p className="text-[11px] text-neutral-400 mb-3">Selecciona qué hojas incluir. El archivo incluye una hoja &quot;Resumen&quot; con los KPIs del periodo seleccionado.</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {SHEET_OPTIONS.map((s) => (
                  <button key={s.id} data-testid={`sheet-toggle-${s.id}`} onClick={() => toggleSheet(s.id)} className={`text-xs px-3 py-1.5 rounded-full border transition ${sheets.includes(s.id) ? "border-emerald-500 bg-emerald-500/10 text-emerald-200" : "border-white/10 text-neutral-400 hover:text-white"}`}><span className="mr-1">{s.icon}</span>{s.label}</button>
                ))}
              </div>
              <button data-testid="export-excel" onClick={exportExcel} disabled={exporting || sheets.length === 0} className="btn-gold px-4 py-2 rounded-full text-xs uppercase tracking-widest flex items-center gap-2 disabled:opacity-50">
                {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Descargar Excel
              </button>
            </Card>
          )}
        </>
      )}
    </div>
  );
};
